import SearchIcon from "@mui/icons-material/Search";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import Layout from "../components/layout/Layout";
import DirectoryFilters, { defaultDirectoryFilters } from "../components/brand/DirectoryFilters";
import DataTable from "../components/brand/DataTable";
import Panel from "../components/brand/Panel";
import SupplierBankFields from "../components/suppliers/SupplierBankFields";
import { useAuth } from "../context/AuthContext";
import { hasSupplierAccess } from "../utils/roleAccess";
import { getSupplier, getSupplierRecords, updateSupplier } from "../api/financeApi";
import { getApiErrorMessage } from "../utils/concurrency";
import { formatDateUk } from "../utils/dateDisplay";
import { bankDetailsForApi, bankDetailsFromSupplier, bankDetailsWereStored, validateBankDetails } from "../utils/supplierBankDetails";

const money = (value) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);
export function SupplierRecords({ supplierId, kind, title, hideHeader = false }) {
  const [data, setData] = useState({ results: [], total: 0 });
  const [filters, setFilters] = useState(defaultDirectoryFilters);
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let current = true;
    setLoading(true); setError("");
    getSupplierRecords(supplierId, kind, page + 1, size, search, kind === "purchase-orders" ? filters : {}).then(({ data: result }) => { if (current) setData(result); })
      .catch((err) => { if (current) setError(getApiErrorMessage(err, `Unable to load ${title.toLowerCase()}.`)); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [supplierId, kind, page, size, title, search, filters]);
  const po = kind === "purchase-orders";
  return <Panel title={hideHeader ? undefined : title} sx={{ mt: hideHeader ? 0 : 3 }}>
    {po && <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", m: 2 }}>
      <Box component="form" onSubmit={(event) => { event.preventDefault(); setSearch(searchDraft.trim()); setPage(0); }} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <TextField label="Search purchase orders" placeholder="Search all fields" value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)} size="small" sx={{ minWidth: 280 }} />
        <Button type="submit" variant="outlined" startIcon={<SearchIcon />}>Search</Button>
      </Box>
      <DirectoryFilters value={filters} onChange={(value) => { setFilters(value); setPage(0); }} />
    </Box>}
    {error && <Alert severity="error">{error}</Alert>}
    {loading ? <CircularProgress aria-label={`Loading ${title}`} sx={{ m: 2 }} /> : <Box sx={{ overflowX: "auto" }}><DataTable aria-label={title}>
      <TableHead><TableRow>{["Reference", ...(supplierId == null ? ["Supplier"] : []), "Property", ...(po ? ["Description", "CoA"] : []), "Gross amount", po ? "From" : "Invoice date", po ? "To" : "Due date", "Status"].map((label) => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
      <TableBody>{data.results.map((row) => <TableRow key={row.id}>
        <TableCell>{po ? row.po_number : row.invoice_number}</TableCell>{supplierId == null && <TableCell>{row.supplier_name}</TableCell>}<TableCell>{row.property_name}</TableCell>
        {po && <><TableCell>{row.description}</TableCell><TableCell>{row.account_code} — {row.account_name}</TableCell></>}
        <TableCell>{money(po ? row.amount : row.gross_amount)}</TableCell><TableCell>{formatDateUk(po ? row.from_date : row.invoice_date)}</TableCell><TableCell>{formatDateUk(po ? row.to_date : row.due_date)}</TableCell><TableCell>{row.status}</TableCell>
      </TableRow>)}{!data.results.length && <TableRow><TableCell colSpan={(po ? 8 : 6) + (supplierId == null ? 1 : 0)}>No {title.toLowerCase()} available.</TableCell></TableRow>}</TableBody>
    </DataTable></Box>}
    <TablePagination component="div" count={data.total} page={page} rowsPerPage={size} rowsPerPageOptions={[10, 25, 50]} onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={(event) => { setSize(Number(event.target.value)); setPage(0); }} />
  </Panel>;
}
export default function SupplierDetailsPage() {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const { user, roleCapabilities } = useAuth();
  const allowed = hasSupplierAccess(user?.claims_role || user?.roles || [], roleCapabilities);
  const [supplier, setSupplier] = useState(null);
  const [error, setError] = useState("");
  const [bankForm, setBankForm] = useState(null);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!allowed) return;
    let current = true;
    setSupplier(null); setError("");
    getSupplier(supplierId).then(({ data }) => { if (current) setSupplier(data); }).catch((err) => { if (current) setError(getApiErrorMessage(err, "Unable to load supplier.")); });
    return () => { current = false; };
  }, [supplierId, allowed]);
  const saveBankDetails = async (event) => {
    event.preventDefault();
    const bankError = validateBankDetails(bankForm);
    if (bankError) { setFormError(bankError); return; }
    const paymentDetails = bankDetailsForApi(bankForm);
    setSaving(true); setFormError("");
    try {
      const { data } = await updateSupplier(supplier.id, { row_version: supplier.row_version, ...paymentDetails });
      const stored = bankDetailsWereStored(data, paymentDetails, supplier);
      if (!stored) {
        setFormError("Payment details could not be stored.");
        setToast({ message: "Payment details could not be stored.", severity: "warning" });
        return;
      }
      setSupplier((current) => ({ ...current, ...data }));
      setToast({ message: "Payment details saved.", severity: "success" });
      setBankForm(null);
    } catch (err) {
      setFormError(getApiErrorMessage(err, "Unable to save payment details. Close and reopen to load the latest version if it has changed."));
    } finally { setSaving(false); }
  };
  const paymentDetails = bankDetailsFromSupplier(supplier || {});
  return <Layout title={supplier?.name || "Supplier"} eyebrow="Supplier details" rightSlot={<Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate("/suppliers")}>Suppliers</Button>}>
    {!allowed ? <Alert severity="error">You do not have access to suppliers.</Alert> : error ? <Alert severity="error">{error}</Alert> : !supplier ? <CircularProgress aria-label="Loading supplier" /> : <>
      <Panel title="Supplier information" noBodyPadding={false}><Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "140px 1fr", gap: 1 }}>
        {Object.entries({ name: "Name", contact_name: "Contact", email: "Contact email", phone: "Phone", address: "Address" }).map(([key, label]) => <React.Fragment key={key}><Typography component="dt" color="text.secondary">{label}</Typography><Typography component="dd" sx={{ m: 0, whiteSpace: "pre-wrap" }}>{supplier[key] || "—"}</Typography></React.Fragment>)}
        <Typography component="dt" color="text.secondary">Status</Typography><Typography component="dd" sx={{ m: 0 }}>{supplier.is_active ? "Active" : "Inactive"}</Typography>
      </Box></Panel>
      <Panel title="Payment details" sx={{ mt: 3 }} noBodyPadding={false} right={<Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => { setFormError(""); setBankForm(bankDetailsFromSupplier(supplier)); }}>Edit payment details</Button>}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Where this supplier is paid.</Typography>
        <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "140px 1fr", gap: 1 }}>
          {[["account_name", "Account name"], ["bank_name", "Bank name"], ["sort_code", "Sort code"], ["account_number", "Account number"]].map(([key, label]) => <React.Fragment key={key}><Typography component="dt" color="text.secondary">{label}</Typography><Typography component="dd" sx={{ m: 0 }}>{paymentDetails[key] || "—"}</Typography></React.Fragment>)}
        </Box>
      </Panel>
      <Dialog open={Boolean(bankForm)} onClose={() => !saving && setBankForm(null)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={saveBankDetails}>
          <DialogTitle>Edit payment details</DialogTitle>
          <DialogContent sx={{ display: "grid", gap: 2, pt: "12px !important" }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <Typography variant="body2" color="text.secondary">Where this supplier is paid. Leave blank if not known yet.</Typography>
            <SupplierBankFields value={bankForm} disabled={saving} onChange={setBankForm} />
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" startIcon={<CloseIcon />} disabled={saving} onClick={() => setBankForm(null)}>Close</Button>
            <Button type="submit" variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}>Save payment details</Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}><Alert severity={toast?.severity || "success"} onClose={() => setToast(null)}>{toast?.message}</Alert></Snackbar>
      <SupplierRecords key={`${supplierId}-po`} supplierId={supplierId} kind="purchase-orders" title="Purchase orders" />
      <SupplierRecords key={`${supplierId}-invoices`} supplierId={supplierId} kind="invoices" title="Invoices" />
    </>}
  </Layout>;
}
